/**
 * 课件导入：选 PDF / 图片 → 渲染成页图 → 建白板页并上传。
 */

import { useRef, useState } from 'react'

import type { Client } from '../lib/client'
import {
  fitBox,
  isOfficeDoc,
  isSupportedDoc,
  renderDocument,
  type ImportProgress,
} from '../lib/docImport'
import { MsgType } from '../lib/messages'
import { Limits } from '../lib/protocol'
import { uploadWhiteboardImage } from '../lib/upload'
import { useRoomStore } from '../stores/roomStore'
import { useWhiteboardStore } from '../stores/whiteboardStore'

interface Props {
  client: Client
  onClose: () => void
}

export function DocImport({ client, onClose }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [progress, setProgress] = useState<ImportProgress | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const selfId = useRoomStore((s) => s.selfId)

  const pick = () => fileRef.current?.click()

  const onFile = async (file: File) => {
    setErr('')

    if (isOfficeDoc(file)) {
      setErr(
        'PPT / Word 请先另存为 PDF 再导入。浏览器里没有可靠的 Office 解析方式，' +
          '转成 PDF 还能保证三端字体与排版一致。',
      )
      return
    }
    if (!isSupportedDoc(file)) {
      setErr('只支持 PDF 与图片文件')
      return
    }

    setBusy(true)
    try {
      const pages = await renderDocument(file, setProgress)
      if (pages.length === 0) {
        setErr('没有可导入的页面')
        return
      }

      const wb = useWhiteboardStore.getState()
      // 课件占满剩余页数就停下，而不是导到一半失败。
      const room = useWhiteboardStore.getState().pages.length
      if (room + pages.length > Limits.MaxPageNum) {
        setErr(
          `白板最多 ${Limits.MaxPageNum} 页，当前已有 ${room} 页，` +
            `无法再导入 ${pages.length} 页。请先删除部分页面。`,
        )
        return
      }

      for (let i = 0; i < pages.length; i++) {
        const p = pages[i]!
        setProgress({ done: i, total: pages.length, label: `上传第 ${i + 1} / ${pages.length} 页` })

        // 每页新建一个白板页，再往里放一张铺满的图片图元。
        const pageIndex = await addPageAndWait(client)
        const box = fitBox(p.width, p.height)

        const objId = await addImageAndWait(client, pageIndex, box, selfId, p.kind ?? 'image')
        if (objId > 0) {
          await uploadWhiteboardImage(client, objId, pageIndex, p.blob)
        }
      }

      // 导入完跳到第一页课件。
      wb.gotoPage(useWhiteboardStore.getState().pages.length - pages.length)
      client.send(MsgType.WBPageGoto, {
        page: useWhiteboardStore.getState().currentPage,
      })
      onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : '导入失败')
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  return (
    <div className="dlg-mask" onClick={busy ? undefined : onClose}>
      <div className="dlg narrow" onClick={(e) => e.stopPropagation()}>
        <h3>导入课件</h3>
        <p className="adm-hint">
          支持 PDF 与图片。每页会渲染成图片放到独立的白板页上，
          你仍可在上面板书标注。
        </p>

        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/*,.pdf,.ppt,.pptx,.doc,.docx"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void onFile(f)
            e.target.value = ''
          }}
        />

        {!busy && (
          <button className="primary block" onClick={pick}>
            选择文件…
          </button>
        )}

        {progress && (
          <div className="import-progress">
            <div className="bar">
              <div
                className="bar-fill"
                style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }}
              />
            </div>
            <span>{progress.label}</span>
          </div>
        )}

        {err && <div className="login-err">{err}</div>}

        <div className="dlg-foot">
          <button onClick={onClose} disabled={busy}>
            {busy ? '导入中…' : '关闭'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * 新建一页并等它出现，返回新页的页号。
 *
 * 页号由服务端分配，客户端不能自己猜 —— 多人同时加页会撞号。
 */
function addPageAndWait(client: Client): Promise<number> {
  return new Promise((resolve) => {
    const before = useWhiteboardStore.getState().pages.length
    const off = client.on<{ page: number }>(MsgType.WBPageAdd, (d) => {
      off()
      resolve(d.page)
    })
    client.send(MsgType.WBPageAdd, { page: 0, title: '' })

    // 服务端没回应时退回到本地推算，避免整个导入卡死。
    setTimeout(() => {
      off()
      resolve(before)
    }, 5000)
  })
}

/** 新建一个图片图元并等服务端回填对象号。 */
function addImageAndWait(
  client: Client,
  page: number,
  box: { x0: number; y0: number; x1: number; y1: number },
  selfId: number,
  kind: 'image' | 'svg' = 'image',
): Promise<number> {
  return new Promise((resolve) => {
    const off = client.on<{ op: string; obj?: { id: number; ownerId: number; kind: string } }>(
      MsgType.WBOp,
      (d) => {
        if (d.op === 'add' && d.obj?.kind === kind && d.obj.ownerId === selfId) {
          off()
          resolve(d.obj.id)
        }
      },
    )
    client.send(MsgType.WBOp, {
      page,
      op: 'add',
      obj: {
        id: 0,
        kind,
        page,
        ownerId: selfId,
        points: [
          { x: box.x0, y: box.y0 },
          { x: box.x1, y: box.y1 },
        ],
        style: {},
        z: 0,
        time: Date.now(),
      },
    })

    setTimeout(() => {
      off()
      resolve(0)
    }, 5000)
  })
}
